import { createContext, useMemo, useState } from 'react';
import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  PaymentMethod,
  StripeError,
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
  StripeExpressCheckoutElementConfirmEvent,
} from '@stripe/stripe-js';
import axios from 'axios';
import classNames from 'classnames';
import {
  GetSubscriptionInfoResult,
  MembershipExtendedPackage,
  MembershipPackage,
  MembershipPackageExtendedPackageName,
  MembershipPackageName,
  MembershipPackageRecurring,
} from 'common/interfaces/api';
import { COIN_SUBSCRIPTION_HELP_URL, isProduction } from 'common/utils';
import { trackGA4Event } from 'common/utils/ga4';
import {
  checkIsChoosingNotSupportPackage,
  checkIsChoosingSubbingMembershipPackage,
  checkIsSubbedIzanaviBefore,
  isChoosingNextMembershipPackage,
  isDownGrade,
  isInitMembershipPackage,
  isPayingIzanavi,
  paymentMembershipPrice,
  subButtonTextModal,
} from 'common/utils/membership';
import { ScreenName } from 'common/utils/pp_tracking';
import {
  a8CvTriggerMembership,
  rentracksCVTrigger,
  trackingMembershipCV,
} from 'common/utils/trackingTag';
import Icon from 'components/common/Icon';
import TeenagePaymentAlertPopup from 'components/common/TeenagePaymentAlertPopup';
import { useQuery } from 'react-query';
import {
  checkPaymentHistoryTeenage,
  createSubscriptionMembership,
  getInitCreditIntent,
  getMembershipPackages,
  setPaymentMethod,
  subExpandPackageByCredit,
} from 'service/payment';
import useGlobalStore from 'store/global';
import useStore from 'store/timeline';

import CoinRemainingSection from '../CoinRemainingSection';
import StripeExpressCheckout from '../StripeExpressCheckout';
import ChoosePaymentMethod from './ChoosePaymentMethod';
import InputCard from './InputCard';
import IzanaviPackageSection from './IzanaviPackageSection';
import LongSubPackageChoose from './LongSubPackageChoose';
import MembershipDescription from './MembershipDescription';
import styles from './MembershipPaymentSection.module.scss';
import PaymentInfo from './PaymentInfo';
import PolicyDescription from './PolicyDescription';

export interface MembershipPaymentSectionContextType {
  currentPackage: MembershipPackage;
  setCurrentPackage: (pack: MembershipPackage) => void;
  membershipPackages: MembershipPackage[];
  screenName: ScreenName;
  isProcessing: boolean;
  currentPaymentBy: 'coin' | 'credit';
  setChoosingIzanaviPackage: (choosing: boolean) => void;
  choosingIzanaviPackage: boolean;
  izanaviPackage: MembershipExtendedPackage | null;
  setPaymentBy: (paymentBy: 'coin' | 'credit') => void;
  renewMode: boolean;
}

export const MembershipPaymentSectionContext =
  createContext<MembershipPaymentSectionContextType | null>(null);

// showMembershipStatus => allow to show cancel button
const MembershipPaymentSection = ({
  initPackage,
  isProcessing,
  setIsProcessing,
  screenName,
  initRecurring,
}: {
  showMembershipStatus: boolean;
  initPackage: MembershipPackageName;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
  screenName: ScreenName;
  initRecurring: MembershipPackageRecurring;
}) => {
  const currentUser = useStore((state) => state.currentUser);
  const isAuthInit = useGlobalStore((state) => state.isAuthInit);
  const setShowMembershipPayDialog = useGlobalStore(
    (state) => state.setShowMembershipPayDialog,
  );
  const [teenAgePaymentErrorReason, setTeenAgePaymentErrorReason] =
    useState<number>(null);
  const setSnackbarMessage = useGlobalStore(
    (state) => state.setSnackbarMessage,
  );
  const isOpenFromTradeOpeningLP = screenName === ScreenName.TakaTradeOpeningLP;
  const refetchPosts = useStore((state) => state.refetchPosts);
  const refetchPost = useStore((state) => state.refetchPost);
  const refetchSymbolData = useStore((state) => state.refetchSymbolData);
  const refetchProfile = useStore((state) => state.refetchProfile);
  const refetchCoinDetails = useStore((state) => state.refetchCoinDetails);
  const setOpenAutoCheckPopup = useGlobalStore(
    (state) => state.setOpenAutoCheckPopup,
  );
  const userCoinDetails = useGlobalStore((state) => state.userCoinDetails);
  const [name, setName] = useState<string>('');
  const [cardError, setCardError] = useState<string>(null);
  const [expiryError, setExpiryError] = useState<string>(null);
  const [cvcError, setCvcError] = useState<string>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [expiryComplete, setExpiryComplete] = useState(false);
  const [cvcComplete, setCvcComplete] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [paymentBy, setPaymentBy] = useState<'coin' | 'credit'>(
    isOpenFromTradeOpeningLP ? 'coin' : 'credit',
  );
  const membership = currentUser?.membership;
  // renewMode => canceled but still have time to use
  const renewMode = useMemo(() => {
    const nextSeconds = new Date(new Date().getTime() + 2 * 1000);
    return (
      !membership?.is_interval &&
      !!membership?.subscription_end_at &&
      new Date(membership?.subscription_end_at) > nextSeconds
    );
  }, [membership?.is_interval, membership?.subscription_end_at]);
  const lastPaymentBy = ['android_coin', 'web_coin', 'ios_coin'].includes(
    membership?.subscription_payment_type,
  )
    ? 'coin'
    : membership?.subscription_payment_type === 'credit'
      ? 'credit'
      : '';
  const currentPaymentBy = lastPaymentBy ? lastPaymentBy : paymentBy;
  const readyToSubMembership = currentUser?.is_membership_subscription_ready;
  const [currentPackage, setCurrentPackage] = useState<MembershipPackage>(null);
  const izanaviPackage = useMemo(() => {
    return currentPackage?.expand_membership_packages?.find(
      (pack) =>
        pack.package_name === MembershipPackageExtendedPackageName.Izanavi,
    );
  }, [currentPackage?.expand_membership_packages]);
  const [userChoosingIzanaviPackage, setChoosingIzanaviPackage] =
    useState(true);
  const choosingIzanaviPackage =
    currentPaymentBy === 'credit' ? userChoosingIzanaviPackage : false;
  // Initialize an instance of stripe.
  const stripe = useStripe();
  const elements = useElements();

  // Check input
  const handleCardNumberChange = (e: StripeCardNumberElementChangeEvent) => {
    setCardError(e.error ? e.error.message : null);
    setCardComplete(e.complete);
  };
  const handleCardExpiryChange = (e: StripeCardExpiryElementChangeEvent) => {
    setExpiryError(e.error ? e.error.message : null);
    setExpiryComplete(e.complete);
  };
  const handleCardCvcChange = (e: StripeCardCvcElementChangeEvent) => {
    setCvcError(e.error ? e.error.message : null);
    setCvcComplete(e.complete);
  };

  const isReSubscription = currentUser?.is_subscribed_membership_before;
  const { data: packagesData } = useQuery(
    ['getMembershipPackages', isAuthInit, screenName],
    async () => {
      if (!isAuthInit) return Promise.reject();
      return await getMembershipPackages();
    },
    {
      enabled: isAuthInit,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        setCurrentPackage(
          isInitMembershipPackage({
            packages: data.data || [],
            initPackageName: initPackage,
            initRecurring,
            subbingMembershipPackage: membership?.membership_package,
          }),
        );
      },
    },
  );
  const membershipPackages = packagesData?.data || [];
  // Get existing card
  const {
    isLoading,
    data,
    refetch: refetchSubscriptionInfo,
  } = useQuery(
    ['getSubscriptionInfo'],
    async () => {
      if (!isAuthInit || !currentUser) return Promise.reject();
      return (await axios.get('/users/accounts/subscription_info'))
        .data as GetSubscriptionInfoResult;
    },
    {
      enabled: isAuthInit && !!currentUser,
      refetchOnWindowFocus: false,
    },
  );
  const paymentMethod = data?.data?.payment_method;
  const isValidInput =
    paymentMethod ||
    (name &&
      !cardError &&
      !expiryError &&
      !cvcError &&
      cardComplete &&
      expiryComplete &&
      cvcComplete);

  // callback when failed subscription
  const onFailedMembershipSubscription = () => {
    setIsProcessing(false);
    if (membership?.subscription_payment_type === 'android_iap') {
      setSnackbarMessage({
        type: 'error',
        text: 'メンバーシップの登録に失敗しました。Androidアプリからメンバーシップを登録してください。',
        autoHideDuration: 10000,
      });
      return;
    } else if (membership?.subscription_payment_type === 'ios_iap') {
      setSnackbarMessage({
        type: 'error',
        text: 'メンバーシップの登録に失敗しました。iOSアプリからメンバーシップを登録してください。',
        autoHideDuration: 10000,
      });
      return;
    }
    setSnackbarMessage({
      type: 'error',
      text: 'メンバーシップ登録が失敗しました',
    });
  };

  const onFailedOptionalMembershipSubscription = () => {
    setIsProcessing(false);
    if (membership?.subscription_payment_type === 'android_iap') {
      setSnackbarMessage({
        type: 'error',
        text: 'IZANAVIの登録に失敗しました。Androidアプリからメンバーシップを登録してください。',
        autoHideDuration: 10000,
      });
      return;
    } else if (membership?.subscription_payment_type === 'ios_iap') {
      setSnackbarMessage({
        type: 'error',
        text: 'IZANAVIの登録に失敗しました。iOSアプリからメンバーシップを登録してください。',
        autoHideDuration: 10000,
      });
      return;
    }
    setSnackbarMessage({
      type: 'error',
      text: 'IZANAVIの登録に失敗しました。',
    });
  };

  // callback when complete subscription
  const onCompleteSubscription = ({
    alertMessage,
  }: {
    alertMessage: string;
  }) => {
    a8CvTriggerMembership(
      currentPackage?.package_name,
      currentUser?.id?.toString(),
    );
    trackingMembershipCV();
    rentracksCVTrigger(currentPackage?.package_name);
    refetchPosts();
    refetchPost();
    refetchSymbolData();
    setSnackbarMessage({
      type: 'success',
      text: alertMessage,
    });
    // need time for sync state of stripe and backend
    setTimeout(function () {
      handleClose();
      refetchProfile();
      refetchCoinDetails();
      if (!paymentMethod && screenName === ScreenName.MembershipPage) {
        void refetchSubscriptionInfo();
      }
      if (currentPaymentBy === 'credit') {
        setOpenAutoCheckPopup(true);
      }
    }, 3000);
  };

  // initCreditCard
  const initCreditCard = async () => {
    try {
      const getIntentResponse = await getInitCreditIntent();
      const cardNumberElement = elements.getElement(CardNumberElement);
      const payment_method = paymentMethod
        ? paymentMethod.id
        : {
            card: cardNumberElement,
            billing_details: {
              name: name,
            },
          };
      const { setupIntent } = await stripe.confirmCardSetup(
        getIntentResponse.data.client_secret,
        {
          payment_method,
        },
      );
      if (!setupIntent || setupIntent.status !== 'succeeded') {
        throw new Error('Failed to create payment method');
      }
      await setPaymentMethod(setupIntent.payment_method);
    } catch (e) {
      setIsProcessing(false);
      setSnackbarMessage({
        type: 'error',
        text: 'カードが登録できませんでした',
      });
      throw new Error('Failed to create payment method');
    }
  };

  const createMembershipSubscription = async () => {
    try {
      await createSubscriptionMembership({
        paymentBy: currentPaymentBy,
        packageId: currentPackage.id,
      });
    } catch (e) {
      onFailedMembershipSubscription();
      throw new Error('Failed to create subscription');
    }
  };

  const createOptionalMembershipSubscription = async () => {
    try {
      await subExpandPackageByCredit({
        packageId: izanaviPackage?.id,
        paymentBy: currentPaymentBy,
      });
    } catch (e) {
      onFailedOptionalMembershipSubscription();
      throw new Error('Failed to create izanavi subscription');
    }
  };

  const willCallSubIzanavi = () =>
    !isPayingIzanavi(currentUser) && !!izanaviPackage && choosingIzanaviPackage;

  const checkMembershipPaymentEligibility = async () => {
    if (!readyToSubMembership) {
      setIsProcessing(false);
      return false;
    }
    if (currentPaymentBy === 'coin' && willCallSubIzanavi()) {
      setSnackbarMessage({
        text: 'IZANAVIはクレジットカードでのみ登録できます',
        type: 'error',
      });
      setIsProcessing(false);
      return false;
    }
    if (willCallSubIzanavi()) {
      if (membership?.subscription_payment_type === 'android_iap') {
        setSnackbarMessage({
          type: 'error',
          text: 'IZANAVIの登録に失敗しました。AndroidアプリからIZANAVIを登録してください。',
          autoHideDuration: 10000,
        });
        setIsProcessing(false);
        return false;
      } else if (membership?.subscription_payment_type === 'ios_iap') {
        setSnackbarMessage({
          type: 'error',
          text: 'IZANAVIの登録に失敗しました。iOSアプリからIZANAVIを登録してください。',
          autoHideDuration: 10000,
        });
        setIsProcessing(false);
        return false;
      }
    }
    const isDown = isDownGrade(membership?.membership_package, currentPackage);
    if (
      !currentUser?.is_adult &&
      !renewMode &&
      !isDown &&
      currentPaymentBy !== 'coin'
    ) {
      try {
        const checkTeenagePaymentResponse = await checkPaymentHistoryTeenage({
          price_amount: currentPackage?.web_amount,
          recurring: currentPackage?.recurring,
          membership_package_id: currentPackage?.id,
          ...(willCallSubIzanavi() && {
            expand_membership_amount: izanaviPackage?.web_amount,
          }),
        });
        const isAbleToPay = checkTeenagePaymentResponse?.data?.is_purchasable;
        if (!isAbleToPay && checkTeenagePaymentResponse?.data?.reason) {
          setTeenAgePaymentErrorReason(
            checkTeenagePaymentResponse?.data?.reason,
          );
          setIsProcessing(false);
          return false;
        }
      } catch (e) {
        setIsProcessing(false);
        return false;
      }
    }
    return true;
  };

  const completeMembershipSubscription = async () => {
    let completeAlertText = '';
    const isChoosingCurrentPackage = checkIsChoosingSubbingMembershipPackage({
      subbingPackage: currentUser?.membership?.membership_package,
      checkingPackage: currentPackage,
    });
    const willCallSubMembership = isChoosingCurrentPackage
      ? !membership?.is_interval
      : true;
    if (willCallSubMembership) {
      await createMembershipSubscription();
      completeAlertText = 'メンバーシップの登録に成功しました';
    }
    if (willCallSubIzanavi()) {
      await createOptionalMembershipSubscription();
      if (willCallSubMembership) {
        completeAlertText = 'メンバーシップとIZANAVIの登録に成功しました';
      } else {
        completeAlertText = 'IZANAVIの登録に成功しました';
      }
    }
    onCompleteSubscription({
      alertMessage: completeAlertText,
    });
  };

  const handleSubscription = async () => {
    const canContinue = await checkMembershipPaymentEligibility();
    if (!canContinue) return;
    try {
      if (!paymentMethod) {
        await initCreditCard();
      }
      await completeMembershipSubscription();
    } catch (e) {
      // empty
      setPaymentFailed(true);
      setIsProcessing(false);
    }
  };

  const handleExpressCheckoutComplete = async (
    _paymentMethod: string | PaymentMethod,
    expressPaymentType: StripeExpressCheckoutElementConfirmEvent['expressPaymentType'],
  ) => {
    await completeMembershipSubscription();
  };

  const handleExpressCheckoutError = (
    _error: Error | StripeError,
    expressPaymentType: StripeExpressCheckoutElementConfirmEvent['expressPaymentType'],
  ) => {
    setIsProcessing(false);
    setSnackbarMessage({
      type: 'error',
      text: 'カードが登録できませんでした',
    });
  };

  const handleClose = () => {
    if (isProcessing) return;
    setName('');
    setPaymentFailed(false);
    setTimeout(function () {
      setIsProcessing(false);
    }, 500);
    setShowMembershipPayDialog(null);
  };

  const paymentMethodInfo = () => {
    if (currentPaymentBy === 'credit') {
      const creditContent = (
        <>
          {(!paymentMethod || paymentFailed) && (
            <StripeExpressCheckout
              disabled={isProcessing || !readyToSubMembership}
              showDivider={!paymentMethod || paymentFailed}
              walletTrackingContext="membership"
              onStart={() => setIsProcessing(true)}
              onBeforeConfirm={checkMembershipPaymentEligibility}
              onComplete={handleExpressCheckoutComplete}
              onError={handleExpressCheckoutError}
            />
          )}
          {!paymentMethod ? (
            <InputCard
              setName={setName}
              name={name}
              handleCardCvcChange={handleCardCvcChange}
              handleCardExpiryChange={handleCardExpiryChange}
              handleCardNumberChange={handleCardNumberChange}
            />
          ) : (
            <div className={styles.existingCard}>
              <div className={styles.ccNumber}>
                **** **** **** {paymentMethod.last4}
              </div>
              <div className={styles.brandIcon}>
                <Icon
                  name={`cc/${paymentMethod.brand}`}
                  width={38}
                  height={26}
                />
              </div>
            </div>
          )}
        </>
      );
      return (
        <div className="mb-3">
          <div className="mb-2 text-left text-xs font-bold leading-[18px] text-[#cccccc]">
            支払い方法
          </div>
          {creditContent}
        </div>
      );
    }
    return (
      <>
        <div className={styles.remainingCoinWrapper}>
          <CoinRemainingSection
            sum={userCoinDetails?.sum}
            consumable={userCoinDetails?.consumable_coin_total}
            onClose={() => setShowMembershipPayDialog(null)}
            smallMode
          />
        </div>
        <div className="mb-3 text-left text-2xs text-white">
          <span className="font-bold">
            ※ コイン支払いでもクレジットカード登録が必要です。
          </span>
          <br />
          コインには有効期限があります。コインの有効期限が切れ、コイン残高が不足した場合にサービスが自動解約されてしまうことを防ぐため、クレジットカード情報の登録をお願いしています。
          <br />
          （コイン残高がある限り、クレジットカードに請求されることはありません。詳しくは
          <a
            className={styles.link}
            href={COIN_SUBSCRIPTION_HELP_URL}
            target="_blank"
          >
            こちら
          </a>
          をご確認ください。 ）
        </div>

        {(!paymentMethod || paymentFailed) && (
          <StripeExpressCheckout
            disabled={isProcessing || !readyToSubMembership}
            showDivider={!paymentMethod || paymentFailed}
            walletTrackingContext="membership"
            onStart={() => setIsProcessing(true)}
            onBeforeConfirm={checkMembershipPaymentEligibility}
            onComplete={handleExpressCheckoutComplete}
            onError={handleExpressCheckoutError}
          />
        )}
        {!paymentMethod ? (
          <InputCard
            setName={setName}
            name={name}
            handleCardCvcChange={handleCardCvcChange}
            handleCardExpiryChange={handleCardExpiryChange}
            handleCardNumberChange={handleCardNumberChange}
          />
        ) : (
          <div className={styles.existingCard}>
            <div className={styles.ccNumber}>
              **** **** **** {paymentMethod.last4}
            </div>
            <div className={styles.brandIcon}>
              <Icon name={`cc/${paymentMethod.brand}`} width={38} height={26} />
            </div>
          </div>
        )}
      </>
    );
  };

  const paymentActionSection = () => {
    if (!currentPackage) return <></>;
    if (!currentUser) return <></>;
    if (membership?.subscription_payment_type === 'ios_iap') {
      return (
        <div className={styles.warningSection}>
          iOSアプリでメンバーシップを行っています。
          {'\n'}
          iOSアプリからメンバーシップを確認してください。
        </div>
      );
    }
    if (membership?.subscription_payment_type === 'android_iap') {
      return (
        <div className={styles.warningSection}>
          Androidアプリでメンバーシップを行っています。
          {'\n'}
          Androidアプリからメンバーシップを確認してください。
        </div>
      );
    }
    const willCallSubIzanavi =
      !isPayingIzanavi(currentUser) &&
      !!izanaviPackage &&
      choosingIzanaviPackage;
    const chargeNumberMembership = paymentMembershipPrice(
      currentPackage,
      currentPaymentBy,
    );
    const chargeNumberIzanavi = willCallSubIzanavi
      ? izanaviPackage?.web_amount
      : 0;
    const isEnoughCoinToSub =
      currentPackage &&
      userCoinDetails?.consumable_coin_total &&
      userCoinDetails?.consumable_coin_total >=
        chargeNumberMembership + chargeNumberIzanavi;
    const isChoosingCurrentPackage = checkIsChoosingSubbingMembershipPackage({
      subbingPackage: currentUser?.membership?.membership_package,
      checkingPackage: currentPackage,
    });
    const isShowingIzanaviSubBtnText =
      !renewMode && willCallSubIzanavi && isChoosingCurrentPackage;
    const actionText = () => {
      if (isShowingIzanaviSubBtnText) {
        return checkIsSubbedIzanaviBefore(currentUser)
          ? 'IZANAVIを再開する'
          : 'IZANAVIを無料体験';
      }
      return subButtonTextModal({
        currentPackage: currentUser?.membership?.membership_package,
        // nextPackage: currentPackage,
        isSubBefore: isReSubscription,
        isRenew: renewMode,
        currentPaymentBy,
      });
    };
    return (
      <>
        {isLoading ? (
          <div className={styles.loading}>
            <Icon name="loading-anim-3" width={24} height={24} />
          </div>
        ) : (
          <>
            {paymentMethodInfo()}
            {isProcessing ? (
              <div
                className={classNames(styles.confirmBtn, {
                  [styles.isIzanaviText]: isShowingIzanaviSubBtnText,
                })}
                style={{
                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                }}
              >
                <Icon name="loading-anim-2" width={24} height={24} />
              </div>
            ) : (
              <>
                {!readyToSubMembership && (
                  <div className={styles.warning}>
                    お支払い状況に問題がありました。
                    <br />
                    詳しくはカスタマーサービスにお問い合わせください。
                  </div>
                )}

                <button
                  type="submit"
                  className={classNames(styles.confirmBtn, {
                    [styles.isIzanaviText]: isShowingIzanaviSubBtnText,
                  })}
                  onClick={() => {
                    if (isProcessing) return;
                    trackGA4Event('click_membership_registration', {
                      screen: screenName || '',
                      selected_plan: currentPackage?.package_name || '',
                      selected_plan_recurring: currentPackage?.recurring || '',
                      selected_plan_level: currentPackage?.level || null,
                      selected_plan_web_amount:
                        currentPackage?.web_amount || null,
                      selected_plan_discounted_amount:
                        currentPackage?.discounted_web_amount || null,
                      prime_subscription_discount_percentage:
                        currentPackage?.prime_subscription_discount_percentage ||
                        null,
                      payment_method: currentPaymentBy,
                      is_izanavi: willCallSubIzanavi,
                      izanavi_amount: willCallSubIzanavi
                        ? izanaviPackage?.web_amount || null
                        : null,
                      button_text: actionText(),
                    });
                    setIsProcessing(true);
                    void handleSubscription();
                  }}
                  disabled={
                    !isValidInput ||
                    (currentPaymentBy === 'coin' &&
                      !isEnoughCoinToSub &&
                      !membership) ||
                    isProcessing ||
                    !readyToSubMembership
                  }
                >
                  {actionText()}
                </button>
              </>
            )}
            <PaymentInfo />
            <PolicyDescription />
          </>
        )}
      </>
    );
  };

  const paymentSection = () => {
    if (!currentPackage) return <></>;
    if (!currentUser) return <></>;
    const isChoosingNextPackage = isChoosingNextMembershipPackage({
      user: currentUser,
      currentPackage,
    });
    const isChoosingCurrentPackage = checkIsChoosingSubbingMembershipPackage({
      subbingPackage: currentUser?.membership?.membership_package,
      checkingPackage: currentPackage,
    });
    const isChoosingNotSupportedPackage = checkIsChoosingNotSupportPackage({
      currentUser,
      choosingPackage: currentPackage,
      izanaviPackage,
      isChoosingIzanaviPackage: choosingIzanaviPackage,
    });
    const willCallSubIzanavi =
      !isPayingIzanavi(currentUser) &&
      !!izanaviPackage &&
      choosingIzanaviPackage;

    // only case renewMode
    const showPaymentForCurrentPackage =
      isChoosingCurrentPackage && (renewMode || willCallSubIzanavi);
    const showPaymentForNextPackage =
      isChoosingNextPackage && willCallSubIzanavi;
    const showPaymentForOtherPackage =
      !isChoosingCurrentPackage &&
      !isChoosingNextPackage &&
      !isChoosingNotSupportedPackage;
    const isShowingPaymentSection =
      showPaymentForOtherPackage ||
      showPaymentForCurrentPackage ||
      showPaymentForNextPackage;
    return (
      <div
        className={styles.formSection}
        style={isProcessing ? { pointerEvents: 'none' } : {}}
      >
        {!membership && <ChoosePaymentMethod />}
        <LongSubPackageChoose />
        {currentPaymentBy === 'credit' && izanaviPackage && (
          <IzanaviPackageSection />
        )}
        {!!izanaviPackage && currentPaymentBy !== 'credit' && (
          <div className={styles.izanaviCoinSubWarning}>
            IZANAVI登録はCoin支払いの対象外です。
          </div>
        )}

        {isChoosingNotSupportedPackage && (
          <div className={styles.izanaviWarning}>
            現在、IZANAVIをご利用中のため、{'\n'}
            メンバーシップ登録のプラン変更はできません。{'\n'}
            IZANAVIはプラチナ限定のオプションであり、{'\n'}
            有効期間が終了するまでは{'\n'}
            メンバーシッププランの変更が制限されます。{'\n'}
            IZANAVIの有効期間が終了後に、{'\n'}再度プラン変更をお試しください。
          </div>
        )}
        {isShowingPaymentSection && paymentActionSection()}
      </div>
    );
  };

  return (
    <MembershipPaymentSectionContext.Provider
      value={{
        currentPackage,
        setCurrentPackage,
        membershipPackages,
        screenName,
        isProcessing,
        currentPaymentBy,
        choosingIzanaviPackage,
        setChoosingIzanaviPackage,
        izanaviPackage,
        setPaymentBy,
        renewMode,
      }}
    >
      <MembershipDescription />
      {/* Right Section */}
      {currentUser && paymentSection()}
      {isProduction() && <span id="a8sales" />}
      {teenAgePaymentErrorReason && (
        <TeenagePaymentAlertPopup
          reason={teenAgePaymentErrorReason}
          setReason={setTeenAgePaymentErrorReason}
        />
      )}
    </MembershipPaymentSectionContext.Provider>
  );
};

export default MembershipPaymentSection;
